/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印按钮组件（photov2）
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-29 16:00:00
 */
import React, { useMemo, useCallback, useState } from 'react'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Button, message, Modal } from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  useAppDispatch,
  useAppSelector,
  useSensorsRole
} from '../../../../app/hooks'
import {
  appendPhotoPickerPhotos,
  setPhotoPickerPhotos
} from '../../../../actions/photoPicker'
import SelectPhotoTip from '../../../components/SelectPhotoTip'
import {
  getSelectionConfig,
  normalizeToSet,
  PhotoSelectionSource,
  useActiveSelection
} from '../../utils/selectionHelpers'

// 定义组件接收的props类型
interface BatchEditWatermarkButtonProps {
  dataSource?: PhotoSelectionSource
  sensorReport?: () => void
}

const BatchEditWatermarkButton: React.FC<BatchEditWatermarkButtonProps> = ({
  dataSource = 'content',
  sensorReport = () => {
    /* noop:默认不上报 */
  }
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const role = useSensorsRole()

  const {
    selectionConfig,
    hasSuggestedSelected,
    activePhotos: photos,
    activeCheckAll: checkAll,
    activeCheckedPhotos: checkedPhotos,
    activeUnCheckedPhotos: unCheckedPhotos
  } = useActiveSelection(dataSource)

  const selectedPhotosFromPicker = useAppSelector((state) => {
    // 检查photoPicker中的from字段是否为batch-editor
    if (state.photoPicker.from === 'batch-editor') {
      return state.photoPicker.photos
    }

    // 否则返回空数组
    return []
  })

  // 状态管理
  // 记录本次批量编辑的照片，仅用于跳转前准备数据
  const [validPhotos, setValidPhotos] = useState<any[]>([])

  // 计算是否禁用按钮
  const disabled = useMemo(() => {
    return checkedPhotos.size === 0 && !checkAll
  }, [checkedPhotos.size, checkAll])

  // 检查照片是否有防伪码
  const hasAntiFakeCode = useCallback((photo: any): boolean => {
    try {
      if (!photo.extension) {
        return false
      }
      const extension =
        typeof photo.extension === 'string'
          ? JSON.parse(photo.extension)
          : photo.extension
      const antiFakeCode = extension?.antiFakeCode
      return antiFakeCode && antiFakeCode.trim().length > 0
    } catch (error) {
      console.error('解析 extension 失败:', error)
      return false
    }
  }, [])

  // 处理批量编辑水印 - 准备数据和验证
  const handleBatchEditWatermark = useCallback(() => {
    if (disabled) return

    // 埋点上报
    sensorReport()
    console.log('photos======', photos)
    // 获取选中照片的完整数据
    let selectedPhotosData: any[] = []

    if (checkAll) {
      // 全选状态，排除未选中的照片
      selectedPhotosData = photos.filter(
        (photo) => !unCheckedPhotos.has(photo.seq)
      )
    } else {
      // 非全选状态，只包含选中的照片
      selectedPhotosData = photos.filter((photo) =>
        checkedPhotos.has(photo.seq)
      )
    }

    // 检查是否有有效照片
    if (selectedPhotosData.length === 0) {
      message.warning('选中的照片中没有包含防伪码的照片，无法进行水印编辑')
      return
    }

    // 检查照片是否有防伪码
    const validPhotos: any[] = selectedPhotosData.filter((photo) =>
      hasAntiFakeCode(photo)
    )
    const invalidPhotos = selectedPhotosData.filter(
      (photo) => !hasAntiFakeCode(photo)
    )

    // 如果所有照片都没有防伪码
    if (validPhotos.length === 0) {
      message.warning('选中的照片中没有包含防伪码的照片，无法进行水印编辑')
      return
    }

    // 如果有部分照片没有防伪码，显示弹窗
    if (invalidPhotos.length > 0) {
      Modal.confirm({
        title: '检测到有缺少防伪码的照片',
        icon: <InfoCircleOutlined />,
        content: `本次共选择${selectedPhotosData.length}张照片，检测到${invalidPhotos.length}张不可编辑，点击继续将会跳过这${invalidPhotos.length}张。`,
        okText: '继续编辑',
        onCancel: () => {
          // 取消操作，不做任何处理
        },
        onOk: () => {
          // 记录有效照片（过滤掉没有防伪码的照片）
          setValidPhotos(validPhotos)

          // 如果 photoPicker 中已有 batch-editor 的数据，则追加；否则覆盖
          if (selectedPhotosFromPicker.length > 0) {
            // 追加选中的照片时，判断是否已存在相同 seq 的照片（使用 seq 作为唯一标识更可靠）
            const existingSeqSet = new Set(
              selectedPhotosFromPicker.map((p) => p.seq)
            )
            const filteredValidPhotos = validPhotos.filter(
              (p) => !existingSeqSet.has(p.seq)
            )

            if (filteredValidPhotos.length === 0) {
              message.warning('已存在相同的照片，请重新选择')
              return
            }

            if (filteredValidPhotos.length !== validPhotos.length) {
              message.warning(
                `已移除 ${
                  validPhotos.length - filteredValidPhotos.length
                } 张重复照片`
              )
            }

            dispatch(appendPhotoPickerPhotos(filteredValidPhotos))
          } else {
            dispatch(setPhotoPickerPhotos(validPhotos, 'batch-editor'))
          }

          // 跳转到水印批量编辑页面
          navigate('/watermark/batchEditor')
        }
      })
      return
    }

    // 所有照片都有防伪码，直接跳转
    // 记录有效照片
    setValidPhotos(validPhotos)

    // 如果 photoPicker 中已有 batch-editor 的数据，则追加；否则覆盖
    if (selectedPhotosFromPicker.length > 0) {
      // 追加选中的照片时，判断是否已存在相同 seq 的照片（使用 seq 作为唯一标识更可靠）
      const existingSeqSet = new Set(selectedPhotosFromPicker.map((p) => p.seq))
      const filteredValidPhotos = validPhotos.filter(
        (p) => !existingSeqSet.has(p.seq)
      )

      if (filteredValidPhotos.length === 0) {
        message.warning('已存在相同的照片，请重新选择')
        return
      }

      if (filteredValidPhotos.length !== validPhotos.length) {
        message.warning(
          `已移除 ${validPhotos.length - filteredValidPhotos.length} 张重复照片`
        )
      }

      dispatch(appendPhotoPickerPhotos(filteredValidPhotos))
    } else {
      dispatch(setPhotoPickerPhotos(validPhotos, 'batch-editor'))
    }

    // 直接跳转到水印批量编辑页面
    navigate('/watermark/batchEditor')
  }, [
    disabled,
    photos,
    checkedPhotos,
    unCheckedPhotos,
    checkAll,
    sensorReport,
    dispatch,
    selectedPhotosFromPicker.length,
    hasAntiFakeCode
  ])

  const Btn = () => {
    const btn = (
      <Button
        type="default"
        ghost={disabled}
        disabled={disabled}
        style={{ marginRight: 10 }}
        onClick={handleBatchEditWatermark}
      >
        批量编辑水印
      </Button>
    )

    return disabled ? <SelectPhotoTip>{btn}</SelectPhotoTip> : btn
  }

  return (
    <>
      <Btn />
    </>
  )
}

export default BatchEditWatermarkButton
